import { type CardsViewerItem } from "components/cardsViewer/types";
import { PeopleEventCard, PeopleFlowCard } from "components/contentAssignment/cards";
import { PeopleEmbeddedVideoCard } from "components/contentAssignment/cards/peopleEmbeddedVideoCard/PeopleEmbeddedVideoCard";
import { PeoplePdfCard } from "components/contentAssignment/cards/peoplePdfCard/PeoplePdfCard";
import {
  AssessmentFilterForm,
  EmbeddedVideosFilterForm,
  FlowFilterForm,
  LinkFilterForm,
  PdfsFilterForm,
  SurveyFilterForm,
  VideosFilterForm,
  ScheduleFilterForm,
} from "components/filterForms";
import { EventFilterForm } from "components/filterForms/EventFilterForm/EventFilterForm";
import { ContentTypesEnum, RouteNames, SortingDirection, ViewType } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import {
  AddAllFlowGroupSuccess,
  AddAllFlowUserSuccess,
  GroupFlowPriorityChangedSuccess,
  RemoveAllFlowGroupPrioritySuccess,
  RemoveAllFlowGroupSuccess,
  RemoveAllFlowUserPrioritiesSuccess,
  RemoveAllFlowUserSuccess,
  SetAllFlowGroupPrioritySuccess,
  SetAllFlowUserPrioritiesSuccess,
  UserFlowPriorityChangedSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { PeopleType } from "features/Library/PeopleAssignments/types";
import { useReloadItemViewListItems } from "hooks/useReloadItemViewListItems";
import { useRtn } from "hooks/useRtn";
import { bindAction } from "interfaces";
import { type ColumnOption } from "interfaces/columnOptions";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "interfaces/onSelectionChanged";
import { isEmpty, noop } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type FiltersMap } from "utils/filterUtils";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import AssessmentsNoResults from "views/library/assessments/Overview/AssessmentsNoResults";
import EmbeddedVideosNoResults from "views/library/embeddedVideos/contentAssignment/EmbeddedVideosNoResults";
import EventsNoResults from "views/library/events/Overview/EventsNoResults";
import FlowsNoResults from "views/library/flows/flowsOverview/FlowsNoResults";
import PdfsNoResults from "views/library/pdfs/contentAssignment/PdfsNoResults";
import SurveysNoResults from "views/library/surveys/Overview/SurveysNoResults";
import { VideoAssetsNoResults } from "views/library/videos/VideoAssetsNoResults/VideoAssetsNoResults";
import {
  resetAppliedFilter,
  resetFilter,
  setAppliedFilter,
  setSearch,
} from "../../features/People/state/slices/contentAssignmentFilterSlice";
import {
  reset,
  resetItems,
  setSelected,
  setSelectedType,
} from "../../features/People/state/slices/contentAssignmentSlice";

import { getColumnOptions, getFlowColumnOptions, getScheduleColumnOptions } from "./getColumnOptions";
import { fetchContent } from "../../features/People/state/thunks/contentAssignmentThunk";
import { fetchContentAssignmentFilters } from "../../features/People/state/thunks/contentAssignmentFilterThunk";
import {
  type ContentAssignmentModelItem,
  ContentColumns,
  type IFlowAssignmentModelItem,
  type IVideoAssignmentModelItem,
  type PeopleContent,
  type SelectedContentMap,
  ScheduleFiltersEnum,
} from "features/People/types";
import { detectHasAnyPermissions } from "./helpers/hasPermissions";
import PeopleOverviewVideoCard from "./cards/peopleVideoCard/PeopleOverviewVideoCard";
import { ExternalLinksNoResults } from "../../features/Library/ExternalLinks/Overview/ExternalLinksNoResults";
import { ContentSegmentationDropdown } from "components/dropdowns";
import SearchInput from "components/searchInput/SearchInput";
import RestrictedAddButton from "components/buttons/addButtonWithPermission/RestrictedAddButton";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ScheduleStatusEnum } from "components/contentAssignment/schedules/scheduleStatusEnum";

const searchInputPlaceholder: {
  [key in ContentTypesEnum]?: string;
} = {
  Embedded: ContentTypesEnum["Embedded Videos"],
  Uploaded: ContentTypesEnum.Videos,
  Scheduled: "Schedules",
};

type Props = PropsFromRedux & {
  entityId: number;
  contentMap: SelectedContentMap;
  contextMenuButtonHandlers: {
    onEditPriority: (item: IFlowAssignmentModelItem) => void;
    onClearPriority: (ids: number[]) => void;
  };
  disabled: boolean;
  showScheduledContent?: boolean;
  peopleType: PeopleType;
  onAddContent: () => void;
};

export const ContentAssignment = (props: Props) => {
  const {
    items,
    isLoading,
    itemsCount,
    resetOverview,
    resetItems,
    onSearchChanged,
    applyFilter,
    resetAppliedFilter,
    resetFilterState,
    filters,
    fetchContentAction,
    fetchFiltersAction,
    entityId,
    areAllLoaded,
    selected,
    contentMap,
    setSelectedIds,
    contextMenuButtonHandlers,
    disabled,
    peopleType,
    onAddContent,
  } = props;
  const contentSchedulingFlag = useFeatureFlag(FeatureFlags.ContentScheduling);
  const contentSchedulingEnabled = !!props.showScheduledContent && !!contentSchedulingFlag;

  const [selectedViewType, setSelectedViewType] = useState<ViewType>(
    contentSchedulingEnabled ? ViewType.LIST : ViewType.GRID,
  );
  const [gridSortBy, setGridSortBy] = useState<[string, SortingDirection]>([...defaultGridSorting]);
  const isGridView = selectedViewType === ViewType.GRID;

  const setDefaultScheduleStatuses = () => {
    props.applyFilter({
      ...props.filters.appliedFilter,
      [ScheduleFiltersEnum.Statuses]: [ScheduleStatusEnum.Pending, ScheduleStatusEnum.Active],
    });
  };

  useEffect(() => {
    if (contentSchedulingEnabled) {
      props.setSelectedTypeAction(ContentTypesEnum.Scheduled);
      setDefaultScheduleStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSchedulingEnabled]);

  useEffect(() => {
    return () => {
      resetFilterState();
      resetOverview();
      contentMap.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetch = (skip: number, top: number, sortBy: string, sortOrder: SortingDirection, fromStart = false) => {
    if (isGridView) {
      fetchGrid(fromStart);
    } else {
      fetchContentAction(entityId, skip, top, sortBy, sortOrder, fromStart, peopleType);
    }
  };

  const fetchGrid = useCallback(
    (fromStart: boolean, sorting?: [string, SortingDirection]) => {
      const [sortBy, sortOrder] = sorting ?? gridSortBy;
      return fetchContentAction(entityId, fromStart ? 0 : items.length, 30, sortBy, sortOrder, !fromStart, peopleType);
    },
    [fetchContentAction, gridSortBy, items.length, entityId, peopleType],
  );
  const refreshContentGrid = useCallback(() => {
    if (!isGridView) return;

    resetItems();
    fetchGrid(true);
  }, [isGridView, resetItems, fetchGrid]);

  const rtnEvents = useMemo(() => (peopleType === PeopleType.User ? usersRTNEvents : groupsRTNEvents), [peopleType]);

  useRtn(rtnEvents, refreshContentGrid);

  const fetchListView = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = ContentColumns.Title,
    sortingDirection: SortingDirection = SortingDirection.Descending,
  ) => {
    const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<ContentColumns>];
    handleFetch(skip, top, sortBy, sortingDirection);
  };

  const [setReloadListItems, handleSearchChange] = useReloadItemViewListItems((term) => {
    onSearchChanged(term);

    if (!isGridView) return;

    resetItems();
    fetchGrid(true);
  });

  const onSortChange = (_: React.SyntheticEvent<HTMLElement>, data: { value: [string, SortingDirection] }) => {
    resetItems();
    setGridSortBy(data.value);
    fetchGrid(true, data.value);
  };

  const onViewTypeChange = (viewType: ViewType) => {
    setSelectedViewType(viewType);
    resetItems();
    viewType === ViewType.GRID && fetchGrid(true);
  };

  const onSelectedItemsChanged = (ids: number[]) => {
    setSelectedIds({ context: props.selectedType, selected: ids });
  };

  const onSelectionChanged = (args: SelectionChangedArgs<PeopleContent>) => {
    const onAdded = (item: PeopleContent) => {
      contentMap?.set(item.id, item);
    };

    const onRemoved = (item: PeopleContent) => {
      contentMap?.delete(item.id);
    };
    HandleOnSelectionChanged(args, onAdded, onRemoved);
  };

  const handleApplyFilter = (filter: FiltersMap) => {
    applyFilter(filter);
    resetItems();

    if (isGridView) fetchGrid(true);
  };

  const handleResetFilter = () => {
    resetAppliedFilter();
    resetItems();
    if (isGridView) fetchGrid(true);
  };

  const handleFiltersFetch = () => {
    fetchFiltersAction(props.selectedType);
  };

  const handleContextChange = (ctx: ContentTypesEnum) => {
    const newContent = contentPropsPovider(
      ctx,
      selected,
      contextMenuButtonHandlers,
      peopleType,
      onAddContent,
      disabled,
    );

    setSelectedViewType((prev) => (newContent.renderCard ? prev : ViewType.LIST));

    resetOverview();
    resetFilterState();
    props.setSelectedTypeAction(ctx);
    if (ctx === ContentTypesEnum.Scheduled) {
      setDefaultScheduleStatuses();
    }
    contentMap.clear();
  };

  const dropdown = (
    <ContentSegmentationDropdown
      onContextChange={handleContextChange}
      defaultValue={props.selectedType}
      showScheduledContent={props.showScheduledContent}
    />
  );

  const {
    getNoResults,
    getColumnOption,
    filterForm: FilterForm,
    renderCard,
  } = useMemo(
    () =>
      contentPropsPovider(props.selectedType, selected, contextMenuButtonHandlers, peopleType, onAddContent, disabled),
    [props.selectedType, selected, contextMenuButtonHandlers, peopleType, onAddContent, disabled],
  );

  const accessRestricted = props.userPermissions && !detectHasAnyPermissions(props.userPermissions, props.selectedType);

  const noResults = useMemo(
    () => getNoResults(!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)),
    [filters.appliedFilter, filters.search, getNoResults],
  );
  const columnOptions = useMemo(() => getColumnOption() as ColumnOption<PeopleContent>[], [getColumnOption]);

  const defaultSortingColumn =
    props.selectedType === ContentTypesEnum.Scheduled ? ContentColumns.ScheduledFor : ContentColumns.Title;
  const sortingDirection =
    props.selectedType === ContentTypesEnum.Scheduled ? SortingDirection.Ascending : SortingDirection.Descending;

  return (
    <GenericItemsView
      accessRestricted={accessRestricted}
      key={props.selectedType}
      items={items}
      viewType={selectedViewType}
      isLoading={isLoading}
      columnOptions={columnOptions}
      fetchData={fetchListView}
      dataCount={itemsCount}
      customHeaderContent={dropdown}
      renderSearch={(accessRestricted) => (
        <SearchInput
          disabled={accessRestricted}
          placeholder={`Search for ${searchInputPlaceholder[props.selectedType] ?? props.selectedType}`}
          onChange={handleSearchChange}
        />
      )}
      // @ts-ignore
      getFilterForm={() => <FilterForm />}
      applyFilter={handleApplyFilter}
      resetFilter={handleResetFilter}
      setReloadListItems={setReloadListItems}
      appliedFilter={filters.appliedFilter}
      filterOptions={filters.filterOptions}
      noResultsContent={noResults}
      filterOptionsLoading={filters.isLoading}
      getFilterOptions={handleFiltersFetch}
      renderCard={renderCard}
      sortOptions={gridSortOptions}
      defaultSortingColumnName={defaultSortingColumn}
      sortingDirection={sortingDirection}
      onSortChange={onSortChange}
      onViewTypeChange={onViewTypeChange}
      disableListViewButton={!renderCard}
      doNotLoadPersistentViewType={contentSchedulingEnabled || !renderCard}
      isAllDataLoaded={areAllLoaded}
      onLoad={isGridView ? () => fetchGrid(true) : noop}
      selectedIds={selected[props.selectedType]}
      onSelectedItemChanged={onSelectedItemsChanged}
      onSelectedGridItemsChanged={onSelectedItemsChanged}
      onSelectionChanged={onSelectionChanged}
      listViewRtnEvents={rtnEvents}
      isSelectDisabled={() =>
        disabled ||
        (props.selectedType !== ContentTypesEnum.Flows &&
          props.selectedType !== ContentTypesEnum.AllFlows &&
          selectedViewType === ViewType.LIST)
      }
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const contentAssignment = state.people.contentAssignment;
  const { content, filters } = contentAssignment;

  return {
    ...content,
    filters,
    selectedType: contentAssignment.content.selectedType,
    userPermissions: state.userProfile.permissions,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetOverview: () => dispatch(reset()),
    resetItems: () => dispatch(resetItems()),
    setSelectedIds: bindAction(setSelected, dispatch),
    setSelectedTypeAction: bindAction(setSelectedType, dispatch),
    fetchContentAction: bindAction(fetchContent, dispatch),
    fetchFiltersAction: bindAction(fetchContentAssignmentFilters, dispatch),
    onSearchChanged: bindAction(setSearch, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetAppliedFilter: bindAction(resetAppliedFilter, dispatch),
    resetFilterState: bindAction(resetFilter, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ContentAssignment);
export default ConnectedComponent;

const columnToParamMap: {
  [P in Lowercase<ContentColumns>]: string;
} = {
  title: "title",
  status: "isDraft",
  publisher: "publisher",
  "priority level": "priority",
  objective: "goalObjectiveId",
  "scheduled for": "startFlowDate",
  trigger: "hasTriggers",
};

const defaultGridSorting = ["dateCreated", SortingDirection.Descending] as const;
const gridSortOptions = [
  {
    text: "Most Recent",
    value: defaultGridSorting,
    default: true,
  },
  {
    text: "Last Modified",
    value: ["dateModified", SortingDirection.Descending],
  },
  {
    text: "Sorted A-Z",
    value: ["title", SortingDirection.Ascending],
  },
];

/* istanbul ignore next */
const getContentPropsMap = (
  selected: { [key in ContentTypesEnum]?: number[] },
  contextMenuButtonHandlers: {
    onEditPriority: (item: IFlowAssignmentModelItem) => void;
    onClearPriority: (ids: number[]) => void;
  },
  peopleType: PeopleType,
  onAddContent: () => void,
  disabled: boolean,
) => {
  const flowProps = {
    filterForm: FlowFilterForm,
    getColumnOption: () =>
      getFlowColumnOptions({
        url: `/${RouteNames.contentFlows}`,
        selectedIds: selected.Flows ?? [],
        contextMenuButtonHandlers,
        peopleType,
        disabled,
      }),
    renderCard: (props: CardsViewerItem<IFlowAssignmentModelItem>) => (
      <PeopleFlowCard
        {...props}
        peopleType={peopleType}
        onEditPriority={() => contextMenuButtonHandlers.onEditPriority(props.item)}
        onRemove={() => contextMenuButtonHandlers.onClearPriority([props.item.id])}
        disablePopupMenu={disabled}
        hasDeepLink
      />
    ),
    getNoResults: (filtered: boolean) => (
      <FlowsNoResults
        title={peopleType === PeopleType.TemplateGroup ? "You don't have any available Flows" : undefined}
        description={
          peopleType === PeopleType.TemplateGroup
            ? "Looks like the owner of this inherited group hasn't added any Flows."
            : undefined
        }
        byCriteria={filtered}
        createFlowButton={
          peopleType !== PeopleType.TemplateGroup ? (
            <RestrictedAddButton buttonText="Add Flow" onClick={onAddContent} hasPermission={!disabled} />
          ) : undefined
        }
      />
    ),
  };

  return {
    [ContentTypesEnum.Flows]: flowProps,
    [ContentTypesEnum.AllFlows]: flowProps,
    [ContentTypesEnum.Scheduled]: {
      filterForm: ScheduleFilterForm,
      getColumnOption: () =>
        getScheduleColumnOptions({
          url: `/${RouteNames.scheduled}`,
          selectedIds: selected.Scheduled ?? [],
          contextMenuButtonHandlers,
          peopleType,
          disabled,
        }),
      renderCard: undefined,
      getNoResults: (filtered: boolean) => (
        <FlowsNoResults
          title={"You don't have any schedules"}
          description={"Looks like you don't have any schedules yet."}
          byCriteria={filtered}
        />
      ),
    },
    [ContentTypesEnum.Uploaded]: {
      filterForm: VideosFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentVideos}`),
      renderCard: (item: CardsViewerItem<IVideoAssignmentModelItem>) => (
        <PeopleOverviewVideoCard
          {...item}
          peopleType={peopleType}
          disablePopupMenu
          selectable={false}
          disabled={disabled}
          deepLink
        />
      ),
      getNoResults: (filtered: boolean) => (
        <VideoAssetsNoResults
          title={peopleType === PeopleType.TemplateGroup ? "You don't have any available videos" : undefined}
          description={
            peopleType === PeopleType.TemplateGroup
              ? "Looks like the owner of this inherited group hasn't added any videos."
              : undefined
          }
          filtered={filtered}
        />
      ),
    },
    [ContentTypesEnum.Events]: {
      filterForm: EventFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentEvents}`),
      renderCard: (item: CardsViewerItem<ContentAssignmentModelItem>) => (
        <PeopleEventCard {...item} peopleType={peopleType} selectable={false} disablePopupMenu deepLink />
      ),
      getNoResults: (filtered: boolean) => (
        <EventsNoResults
          title={peopleType === PeopleType.TemplateGroup ? "You don't have any available events" : undefined}
          description={
            peopleType === PeopleType.TemplateGroup
              ? "Looks like the owner of this inherited group hasn't added any events."
              : undefined
          }
          filtered={filtered}
        />
      ),
    },
    [ContentTypesEnum.PDFs]: {
      filterForm: PdfsFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentPdfs}`),
      renderCard: (item: CardsViewerItem<ContentAssignmentModelItem>) => (
        <PeoplePdfCard {...item} peopleType={peopleType} selectable={false} deepLink />
      ),
      getNoResults: (filtered: boolean) => (
        <PdfsNoResults
          title={peopleType === PeopleType.TemplateGroup ? "You don't have any available PDFs" : undefined}
          description={
            peopleType === PeopleType.TemplateGroup
              ? "Looks like the owner of this inherited group hasn't added any PDFs."
              : undefined
          }
          filtered={filtered}
        />
      ),
    },
    [ContentTypesEnum.Assessments]: {
      filterForm: AssessmentFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentAssessments}`),
      getNoResults: (filtered: boolean) => (
        <AssessmentsNoResults
          title={peopleType === PeopleType.TemplateGroup ? "You don't have any available assessments" : undefined}
          description={
            peopleType === PeopleType.TemplateGroup
              ? "Looks like the owner of this inherited group hasn't added any assessments."
              : undefined
          }
          filtered={filtered}
        />
      ),
      renderCard: undefined,
    },
    [ContentTypesEnum.Surveys]: {
      filterForm: SurveyFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentSurveys}`),
      getNoResults: (filtered: boolean) => (
        <SurveysNoResults
          title={peopleType === PeopleType.TemplateGroup ? "You don't have any available surveys" : undefined}
          description={
            peopleType === PeopleType.TemplateGroup
              ? "Looks like the owner of this inherited group hasn't added any surveys."
              : undefined
          }
          filtered={filtered}
        />
      ),
      renderCard: undefined,
    },
    [ContentTypesEnum.Embedded]: {
      filterForm: EmbeddedVideosFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentEmbeddedVideos}`),
      renderCard: (item: CardsViewerItem<ContentAssignmentModelItem>) => (
        <PeopleEmbeddedVideoCard {...item} peopleType={peopleType} deepLink />
      ),
      getNoResults: (filtered: boolean) => (
        <EmbeddedVideosNoResults
          title={peopleType === PeopleType.TemplateGroup ? "You don't have any available embedded videos" : undefined}
          description={
            peopleType === PeopleType.TemplateGroup
              ? "Looks like the owner of this inherited group hasn't added any embedded videos."
              : undefined
          }
          filtered={filtered}
        />
      ),
    },
    [ContentTypesEnum.Links]: {
      filterForm: LinkFilterForm,
      getColumnOption: () => getColumnOptions(`/${RouteNames.contentLinks}`),
      getNoResults: (filtered: boolean) => {
        let description: string = "";
        if (peopleType === PeopleType.TemplateGroup) {
          description = "Looks like the owner of this inherited group hasn't added any link assets.";
        } else if (peopleType === PeopleType.User) {
          description = "Looks like you don't have any link assets for this user.";
        } else {
          description = "Looks like you don't have any link assets for this group.";
        }
        return (
          <ExternalLinksNoResults
            filtered={filtered}
            title={peopleType === PeopleType.TemplateGroup ? "You don't have any available links" : undefined}
            description={description}
          />
        );
      },
      renderCard: undefined,
    },
  };
};

type ContentProps = ReturnType<typeof getContentPropsMap>[keyof ReturnType<typeof getContentPropsMap>];
type ContentMap = {
  [K in ContentTypesEnum]?: ContentProps;
};

const contentPropsPovider = (
  ctx: ContentTypesEnum,
  selected: { [key in ContentTypesEnum]?: number[] },
  contextMenuButtonHandlers: {
    onEditPriority: (item: IFlowAssignmentModelItem) => void;
    onClearPriority: (ids: number[]) => void;
  },
  peopleType: PeopleType,
  onAddContent: () => void,
  disabled: boolean,
): ContentProps => {
  const map: ContentMap = getContentPropsMap(selected, contextMenuButtonHandlers, peopleType, onAddContent, disabled);
  return map[ctx]!;
};

const usersRTNEvents = [
  AddAllFlowUserSuccess,
  SetAllFlowUserPrioritiesSuccess,
  RemoveAllFlowUserSuccess,
  UserFlowPriorityChangedSuccess,
  RemoveAllFlowUserPrioritiesSuccess,
];
const groupsRTNEvents = [
  AddAllFlowGroupSuccess,
  SetAllFlowGroupPrioritySuccess,
  RemoveAllFlowGroupSuccess,
  GroupFlowPriorityChangedSuccess,
  RemoveAllFlowGroupPrioritySuccess,
];
